import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';

import { calculateDiscountPercent, formatDateTODMY, formatDateTODMYHMA, getReadableErrorMessage, isPastDate } from '../../../utils/utils';
import MediaCarousel from '../comp/MediaCarousel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as fullStar } from '@fortawesome/free-solid-svg-icons'
import { faStarHalfAlt, faStar as emptyStar } from '@fortawesome/free-regular-svg-icons'

import CustomLoader from '../../common/CustomLoader';


export default function ProductDetails() {
    const location = useLocation();
    const product = location.state?.product;


    const media = [
        ...(product?.images || []).map((img) => ({ type: "image", url: img })),
        ...(product?.videos || []).map((vid) => ({ type: "video", url: vid })),
    ];

    // Per review Only one reply is Allowed By Seller
    const [isReviewsLoading, setIsReviewsLoading] = useState(false);
    const [error, setError] = useState("");

    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        // Last 5 reviews
        const getProductReviews = async () => {
            setIsReviewsLoading(true);
            try {
                const reviewsRef = collection(db, "reviews");
                let queryConstraints = [];
                queryConstraints.push(orderBy("createdAt", "desc"));
                queryConstraints.push(where("productIds", 'array-contains', product.id));
                const reviewsQuery = query(reviewsRef, ...queryConstraints);
                const reviewSnap = await getDocs(reviewsQuery);
                const fetchedReviews = [];
                for (const doc of reviewSnap.docs) {
                    fetchedReviews.push(doc.data());
                }
                setReviews(fetchedReviews);
                setIsReviewsLoading(false);
                setError("");
            }
            catch (e) {
                let errorsMsg = getReadableErrorMessage(e.code || e.message);
                setError(errorsMsg);
                setIsReviewsLoading(false);
            }
            finally {
                setIsReviewsLoading(false);
            }
        };

        if (product !== null) {
            getProductReviews();
        }
    },
        [product]);

    if (!product) {
        return <div>
            <p>No Product data found!</p>
        </div>;
    }

    return (
        <div className="mx-auto">

            <div className="flex flex-col lg:flex-row gap-4">
                {/* Media: Images and Video Carousel */}
                <div className="w-full lg:w-1/2">
                    <MediaCarousel media={media} />
                </div>

                {/* Other Details */}
                <div className="w-full lg:w-1/2 text-medium-text-color">
                    <h2 className="text-xl font-semibold mb-4">Product Details</h2>
                    {/* Name */}
                    <div className='mb-2 text-lg'>
                        Title: <span className="font-semibold">{product.name}</span> ({product.id.split("-")[1]})
                    </div>
                    {/* Date Created */}
                    <div className='mb-2 text-lg'>
                        Created At: <span className="font-semibold">{formatDateTODMY(product.addedDateTime)}</span>
                    </div>
                    {/* Category */}
                    <div className='mb-2 text-lg'>
                        Category: <span className="font-semibold">{product.categoriesNames.join(" > ")}</span>
                    </div>
                    {/* Specifications */}
                    {product.specifications && Object.keys(product.specifications).length > 0 && (
                        <div className="mb-2">
                            <h2 className="text-lg font-semibold mb-2">Specifications</h2>
                            <table className="w-full border-collapse border border-border">
                                <tbody>
                                    {Object.entries(product.specifications).map(([key, value], index) => (
                                        <tr key={index} className="border border-border">
                                            <td className="px-4 py-2 font-medium bg-sidebar-bg">{key}</td>
                                            <td className="px-4 py-2 bg-white">{value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {/* Variants Length */}
                    <div className='mb-2 text-lg'>
                        Number of variants: <span className="font-semibold">{product.variants.length}</span>
                    </div>
                    {/* Is Deleted */}
                    <div className='mb-2 text-lg'>
                        Status: <span>
                            {
                                product.isDeleted ?
                                    <span>
                                        <span className="font-semibold">Deleted, </span>
                                        Deleted At: <span className="font-semibold">{formatDateTODMY(product.deleteDateTime || product.updatedDateTime || product.addedDateTime)}</span>
                                    </span>
                                    :
                                    <span className="font-semibold">
                                        Active
                                    </span>
                            }
                        </span>
                    </div>
                    {/* Updated At */}
                    <div className='mb-2 text-lg'>
                        Updated At: <span className="font-semibold">{formatDateTODMY(product.updatedDateTime || product.addedDateTime)}</span>
                    </div>

                </div>
            </div>


            <div className="mt-4 text-medium-text-color">
                {/* Variants */}
                <div className='mb-2 text-lg'>
                    <h2 className="text-xl font-semibold mb-2">Variants</h2>
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
                        {product.variants.map((variant, index) => {
                            const isSpecialPriceValid = !isPastDate(variant.timeForSpecialPrice);
                            return (
                                <div
                                    key={variant.id}
                                    className="p-4 border rounded-lg shadow-md bg-white text-base"
                                >
                                    <div>
                                        Sr: <span className='font-semibold'>{index + 1}</span>
                                    </div>
                                    <div>
                                        Name: <span className='font-semibold'>{variant.name}</span> ({variant.id.split("-")[1]})
                                    </div>
                                    <div>
                                        Stock: <span className='font-semibold'>{variant.stock}</span>
                                    </div>
                                    <div>
                                        Price: <span className='font-semibold text-bright-green'>PKR {variant.price}</span>
                                    </div>
                                    {isSpecialPriceValid && (
                                        <>
                                            <div>
                                                Special Price: <span className="font-semibold text-bright-green">PKR {variant.specialPrice}</span>
                                            </div>
                                            <div>
                                                Discount: <span className="font-semibold">{calculateDiscountPercent(variant.price, variant.specialPrice)}%</span>
                                            </div>
                                            <div>
                                                Special Price Valid Until: <span className="font-semibold">{formatDateTODMY(variant.timeForSpecialPrice)}</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Description */}
                <div className='mb-2 text-base'>
                    <h2 className="text-xl font-semibold mb-2">Description</h2>
                    {product.description}
                </div>
                {/* Keywords */}
                <div className='mb-2 text-base'>
                    <h2 className="text-xl font-semibold mb-2">Keywords</h2>
                    {product.keywords.join(", ")}
                </div>

                {/* Reviews */}
                <div className='mb-2'>
                    <div className='text-base flex items-center gap-2'>
                        <h2 className="text-xl font-semibold">Reviews</h2>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: 5 }, (_, i) => {
                                const ratingValue = i + 1;
                                return (
                                    <span key={i}>
                                        {product.ratings >= ratingValue ? (
                                            <FontAwesomeIcon icon={fullStar} color='orange' />
                                        ) : product.ratings >= ratingValue - 0.5 ? (
                                            <FontAwesomeIcon icon={faStarHalfAlt} color='orange' />
                                        ) : (
                                            <FontAwesomeIcon icon={emptyStar} color='orange' />
                                        )}
                                    </span>
                                );
                            })}
                            <span className="ml-2 text-sm text-gray-600">
                                {product.ratings} ({product.ratingsCount})
                            </span>
                        </div>
                    </div>

                    {/* Reviews */}
                    <div className='mb-4 mt-4 flex flex-1 justify-center items-center'>
                        {isReviewsLoading ? (
                            <CustomLoader />
                        ) : error.length > 0 ? (
                            <p className="text-error-color text-sm mt-1 mb-1 mx-3" style={{ whiteSpace: 'pre-line' }}>{error}</p>
                        ) : reviews.length === 0 ? (
                            <p className='font-medium text-light-text-color'>No Reviews found.</p>
                        ) : (
                            <div className="flex-1 overflow-auto">
                                {reviews.map((review, index) => (
                                    <div key={index} className="flex justify-between p-4 border-b">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-semibold">{review.userName}</h3>
                                                <p>
                                                    {review.highlightedProducts.map((item) => (
                                                        item.productId === product.id && (
                                                            <span key={item.productId} className="text-yellow-500">
                                                                {Array.from({ length: 5 }, (_, i) => {
                                                                    const ratingValue = i + 1;
                                                                    return (
                                                                        <span key={i}>
                                                                            {item.ratings >= ratingValue ? (
                                                                                <FontAwesomeIcon icon={fullStar} color="orange" />
                                                                            ) : item.ratings >= ratingValue - 0.5 ? (
                                                                                <FontAwesomeIcon icon={faStarHalfAlt} color="orange" />
                                                                            ) : (
                                                                                <FontAwesomeIcon icon={emptyStar} color="orange" />
                                                                            )}
                                                                        </span>
                                                                    );
                                                                })}
                                                                <span className="ml-2 text-sm text-gray-600">
                                                                    ({item.ratings})
                                                                </span>
                                                            </span>
                                                        )
                                                    ))}
                                                </p>
                                            </div>
                                            <p className="text-gray-600">{review.orderFeedback}</p>

                                            {/* Seller reply section */}
                                            {review.hasSellerReply ? (
                                                <div className="mt-4 p-4 border-t">
                                                    <h4 className="font-semibold">Seller's Reply:</h4>
                                                    <p className="text-gray-600">{review.sellerReply}</p>
                                                    <p className="text-gray-500 text-sm">Replied on {formatDateTODMYHMA(review.sellerReplyAt)}</p>
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                        <div className="text-gray-500 text-sm">
                                            {formatDateTODMYHMA(review.createdAt)}
                                        </div>
                                    </div>
                                ))}
                            </div>

                        )}
                    </div>

                </div>
            </div>
        </div>
    );
};
